'use client';

import { useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import { MOCK_CAR_MAKES, type CarMake, type CarModel } from '@/app/lib/mock-db';
import { useRouter } from 'next/navigation';
import { Card } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Search, Calendar, Car, SlidersHorizontal, MapPin, CircleDollarSign, X } from 'lucide-react';
import { fetchCarMakes } from '@/lib/car-makes';
import { fetchCarModels, fetchCarTypes, type CarTypeOption } from '@/lib/car-taxonomy';
import BrandLogoImage from '@/components/BrandLogoImage';
import { useCountry } from '@/context/CountryContext';
import { getCitiesForCountry } from '@/lib/country-cities';
import {
  DEFAULT_CAR_PRICE_FILTER_CONFIG,
  fetchPublicSettings,
  getCarPriceFilterConfig,
} from '@/lib/public-settings';

const BODY_TYPE_IMAGE_BY_KEYWORD: Array<{ keywords: string[]; imageUrl: string }> = [
  { keywords: ['sedan', 'سيدان'], imageUrl: '/assets/car_types/sedan.png' },
  { keywords: ['suv', 'دفع رباعي'], imageUrl: '/assets/car_types/suv.png' },
  { keywords: ['pickup', 'بيك'], imageUrl: '/assets/car_types/pickup.png' },
  { keywords: ['truck', 'شاحنة'], imageUrl: '/assets/car_types/truck.png' },
  { keywords: ['hatchback', 'هاتشباك'], imageUrl: '/assets/car_types/hatchback.png' },
  { keywords: ['coupe', 'كوبيه'], imageUrl: '/assets/car_types/coupe.png' },
  { keywords: ['convertible', 'مكشوف'], imageUrl: '/assets/car_types/convertible.png' },
  { keywords: ['luxury', 'فاخر'], imageUrl: '/assets/car_types/luxury.png' },
  { keywords: ['sport', 'رياضي'], imageUrl: '/assets/car_types/sport.png' },
  { keywords: ['electric', 'ev', 'كهرب'], imageUrl: '/assets/car_types/electric.png' },
  { keywords: ['van', 'فان'], imageUrl: '/assets/car_types/van.png' },
];

function normalizeBodyTypeKey(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]/g, '');
}

function getBodyTypeImage(type: CarTypeOption): string | null {
  const candidates = [type.id, type.code, type.name_en, type.name_ar].map(normalizeBodyTypeKey).filter(Boolean);
  const match = BODY_TYPE_IMAGE_BY_KEYWORD.find((item) => (
    item.keywords.some((keyword) => {
      const key = normalizeBodyTypeKey(keyword);
      return candidates.some((candidate) => candidate.includes(key) || key.includes(candidate));
    })
  ));
  return match?.imageUrl || type.image_url || null;
}

function BodyTypeArtwork({ type }: { type: CarTypeOption }) {
  const imageUrl = getBodyTypeImage(type);
  if (!imageUrl) return <Car className="h-10 w-10 text-primary" />;

  return (
    <span
      aria-hidden="true"
      className="block h-16 w-28 bg-gradient-to-br from-primary to-secondary"
      style={{
        WebkitMaskImage: `url("${imageUrl}")`,
        maskImage: `url("${imageUrl}")`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }}
    />
  );
}

export default function BuyMakePage() {
  const router = useRouter();
  const { selectedCountryCode, selectedCountry } = useCountry();
  const [carMakes, setCarMakes] = useState<CarMake[]>(MOCK_CAR_MAKES);
  const [carTypes, setCarTypes] = useState<CarTypeOption[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [selectedMake, setSelectedMake] = useState<CarMake | null>(null);
  const [selectedType, setSelectedType] = useState<CarTypeOption | null>(null);
  const [step, setStep] = useState<'body' | 'brand'>('body');
  const [searchModel, setSearchModel] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [quickSearch, setQuickSearch] = useState('');
  const [quickMakeId, setQuickMakeId] = useState('__all');
  const [quickModelId, setQuickModelId] = useState('__all');
  const [priceFilterConfig, setPriceFilterConfig] = useState(DEFAULT_CAR_PRICE_FILTER_CONFIG);
  const [quickPriceRange, setQuickPriceRange] = useState<number[]>([
    DEFAULT_CAR_PRICE_FILTER_CONFIG.min,
    DEFAULT_CAR_PRICE_FILTER_CONFIG.max,
  ]);
  const [quickCity, setQuickCity] = useState('__all');

  useEffect(() => {
    let alive = true;
    Promise.all([fetchCarMakes(), fetchCarModels(), fetchCarTypes()]).then(([makeRows, modelRows, typeRows]) => {
      if (!alive) return;
      if (makeRows.length > 0) setCarMakes(makeRows);
      if (modelRows.length > 0) setCarModels(modelRows);
      if (typeRows.length > 0) setCarTypes(typeRows);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchPublicSettings(selectedCountryCode).then((payload) => {
      if (!alive) return;
      const nextConfig = getCarPriceFilterConfig(payload);
      setPriceFilterConfig(nextConfig);
      setQuickPriceRange((current) => {
        const nextStart = Math.min(Math.max(current[0], nextConfig.min), nextConfig.max);
        const nextEnd = Math.max(nextStart, Math.min(Math.max(current[1], nextConfig.min), nextConfig.max));
        return [nextStart, nextEnd];
      });
    });
    return () => { alive = false; };
  }, [selectedCountryCode]);

  const filteredModels = carModels.filter(model => 
    model.make_id === selectedMake?.id &&
    (model.name_ar.includes(searchModel) || model.name_en.toLowerCase().includes(searchModel.toLowerCase()))
  );

  const quickModels = carModels.filter(model => quickMakeId !== '__all' && model.make_id === quickMakeId);

  const hasQuickFilters =
    quickSearch.trim() ||
    quickMakeId !== '__all' ||
    quickModelId !== '__all' ||
    quickPriceRange[0] !== priceFilterConfig.min ||
    quickPriceRange[1] !== priceFilterConfig.max ||
    quickCity !== '__all';

  const addQuickFiltersToParams = (params: URLSearchParams) => {
    if (quickSearch.trim()) params.set('search', quickSearch.trim());
    if (quickPriceRange[0] !== priceFilterConfig.min) params.set('priceMin', String(quickPriceRange[0]));
    if (quickPriceRange[1] !== priceFilterConfig.max) params.set('priceMax', String(quickPriceRange[1]));
    if (quickCity !== '__all') params.set('city', quickCity);
  };

  const handleApplyQuickFilters = () => {
    const params = new URLSearchParams();
    if (quickMakeId !== '__all') params.set('make', quickMakeId);
    if (quickModelId !== '__all') params.set('model', quickModelId);
    addQuickFiltersToParams(params);
    router.push(`/buy/results?${params.toString()}`);
  };

  const clearQuickFilters = () => {
    setQuickSearch('');
    setQuickMakeId('__all');
    setQuickModelId('__all');
    setQuickPriceRange([priceFilterConfig.min, priceFilterConfig.max]);
    setQuickCity('__all');
  };

  const cityOptions = getCitiesForCountry(selectedCountryCode);

  const handleApplyFilters = (modelId?: string) => {
    const params = new URLSearchParams();
    if (selectedMake) params.set('make', selectedMake.id);
    if (selectedType) params.set('type', selectedType.id);
    if (modelId) params.set('model', modelId);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    addQuickFiltersToParams(params);
    
    router.push(`/buy/results?${params.toString()}`);
    setSelectedMake(null);
  };

  const handleSelectType = (type: CarTypeOption) => {
    setSelectedType(type);
    setStep('brand');
  };

  const handleBackToTypes = () => {
    setStep('body');
    setSelectedMake(null);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title={step === 'body' ? 'اختر نوع الهيكل' : 'اختر ماركة السيارة'} />
      
      <div className="p-4 max-w-4xl mx-auto space-y-6">
        <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold font-headline mb-2">
                {step === 'body' ? 'الخطوة 1: نوع الهيكل' : 'الخطوة 2: الماركة'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {step === 'body'
                  ? 'اختر شكل السيارة أولاً، ثم سنعرض لك الماركات في الصفحة التالية'
                  : `اختر الماركة المناسبة${selectedType ? ` لسيارات ${selectedType.name_ar}` : ''}`}
              </p>
            </div>
            {step === 'brand' && (
              <Button
                variant="outline"
                className="rounded-2xl shrink-0"
                onClick={handleBackToTypes}
              >
                رجوع
              </Button>
            )}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="h-1.5 rounded-full bg-primary" />
            <div className={`h-1.5 rounded-full ${step === 'brand' ? 'bg-secondary' : 'bg-slate-200'}`} />
          </div>
        </div>

        <div className="rounded-[1.5rem] bg-white p-3 shadow-sm border border-slate-100">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-black text-slate-700">
              <SlidersHorizontal className="h-4 w-4 text-primary" />
              فلترة سريعة
            </div>
            {hasQuickFilters && (
              <Button variant="ghost" size="sm" onClick={clearQuickFilters} className="h-8 rounded-full px-3 text-xs font-bold text-slate-500 hover:text-red-500">
                <X className="ml-1 h-3.5 w-3.5" />
                مسح
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="relative sm:col-span-2 lg:col-span-6">
              <Search className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="h-12 rounded-xl border-slate-100 bg-slate-50 pr-11 text-sm font-bold"
                placeholder="ابحث عن سيارة، موديل، سنة، أو كلمة في الإعلان..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleApplyQuickFilters();
                }}
              />
            </div>

            <Select
              value={quickMakeId}
              onValueChange={(value) => {
                setQuickMakeId(value);
                setQuickModelId('__all');
              }}
            >
              <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                <SelectValue placeholder="الماركة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">كل الماركات</SelectItem>
                {carMakes.map((make) => (
                  <SelectItem key={make.id} value={make.id}>{make.name_ar}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={quickModelId}
              onValueChange={setQuickModelId}
              disabled={quickMakeId === '__all'}
            >
              <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                <SelectValue placeholder="الموديل" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">كل الموديلات</SelectItem>
                {quickModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>{model.name_ar}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 sm:col-span-2">
              <div className="mb-3 flex items-center justify-between gap-3 text-xs font-black text-slate-600">
                <span className="flex items-center gap-1">
                  <CircleDollarSign className="h-4 w-4 text-primary" />
                  السعر
                </span>
                <span dir="ltr" className="text-primary">
                  {quickPriceRange[0].toLocaleString()} - {quickPriceRange[1].toLocaleString()} {selectedCountry?.currency}
                </span>
              </div>
              <Slider
                min={priceFilterConfig.min}
                max={priceFilterConfig.max}
                step={priceFilterConfig.step}
                value={quickPriceRange}
                onValueChange={setQuickPriceRange}
              />
            </div>

            <Select value={quickCity} onValueChange={setQuickCity}>
              <SelectTrigger className="h-11 rounded-xl border-slate-100 bg-slate-50 text-sm font-bold">
                <SelectValue placeholder="المدينة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">كل المدن</SelectItem>
                {cityOptions.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={handleApplyQuickFilters} className="h-11 rounded-xl gap-2 font-bold">
              <MapPin className="h-4 w-4" />
              تطبيق
            </Button>
          </div>
        </div>

        {step === 'body' && carTypes.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold font-headline">نوع الهيكل</h2>
                <p className="text-sm text-muted-foreground">اختيار نوع الهيكل ينقلك مباشرة إلى الماركات</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {carTypes.map((type) => {
                const isSelected = selectedType?.id === type.id;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleSelectType(type)}
                    className="text-right"
                  >
                    <Card className={`p-5 flex min-h-36 flex-col items-center justify-center border transition-all rounded-[2rem] bg-white group w-full h-full ${
                      isSelected
                        ? 'border-primary shadow-lg shadow-primary/10'
                        : 'border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1'
                    }`}>
                      <div className="mb-3 flex h-20 w-full items-center justify-center rounded-3xl bg-gradient-to-br from-blue-50 to-orange-50 transition-transform group-hover:scale-105">
                        <BodyTypeArtwork type={type} />
                      </div>
                      <span className="font-bold text-slate-800">{type.name_ar}</span>
                      <span className="text-[10px] text-slate-400 font-bold uppercase mt-1">{type.name_en}</span>
                    </Card>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'brand' && (
          <section className="space-y-3">
            {selectedType && (
              <div className="inline-flex items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-bold text-primary">
                <Car className="h-4 w-4" />
                {selectedType.name_ar}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {carMakes.map((make) => (
                <button key={make.id} onClick={() => setSelectedMake(make)} className="text-right">
                  <Card className="p-6 flex flex-col items-center justify-center border-none shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all rounded-[2.5rem] bg-white group w-full h-full">
                    <div className="relative w-20 h-20 mb-4 transition-transform group-hover:scale-110">
                      <BrandLogoImage
                        brand={make}
                        alt={make.name_ar}
                        className="w-full h-full"
                      />
                    </div>
                    <span className="font-bold text-slate-800">{make.name_ar}</span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase mt-1">{make.name_en}</span>
                  </Card>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <Dialog open={!!selectedMake} onOpenChange={(open) => !open && setSelectedMake(null)}>
        <DialogContent className="rounded-[2.5rem] max-w-2xl p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-6 bg-slate-900 text-white">
            <div className="flex items-center gap-4">
              {selectedMake && (
                <div className="relative w-12 h-12 bg-white rounded-xl p-2">
                  <BrandLogoImage
                    brand={selectedMake}
                    alt={selectedMake.name_ar}
                    className="w-full h-full p-1"
                  />
                </div>
              )}
              <DialogTitle className="text-xl font-bold font-headline">
                {selectedMake?.name_ar} - اختر الموديل والسنة
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Year Filter */}
            <div className="space-y-3">
              <label className="text-sm font-bold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" /> سنة الصنع
              </label>
              <div className="grid grid-cols-2 gap-4">
                <Input 
                  placeholder="من سنة (مثلاً 2015)" 
                  type="number" 
                  value={yearFrom}
                  onChange={(e) => setYearFrom(e.target.value)}
                  className="rounded-2xl h-12 bg-slate-50 border-none"
                />
                <Input 
                  placeholder="إلى سنة (مثلاً 2024)" 
                  type="number" 
                  value={yearTo}
                  onChange={(e) => setYearTo(e.target.value)}
                  className="rounded-2xl h-12 bg-slate-50 border-none"
                />
              </div>
            </div>

            {/* Model Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold flex items-center gap-2">
                  <Car className="h-4 w-4 text-primary" /> الموديلات المتوفرة
                </label>
                <div className="relative w-48">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                  <Input 
                    placeholder="ابحث عن موديل..." 
                    className="h-9 pr-8 rounded-xl text-xs bg-slate-50 border-none"
                    value={searchModel}
                    onChange={(e) => setSearchModel(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => handleApplyFilters()}
                  className="p-4 rounded-2xl border-2 border-dashed border-slate-200 hover:border-primary hover:bg-blue-50 transition-all text-center group"
                >
                  <span className="block font-bold text-sm group-hover:text-primary">جميع موديلات {selectedMake?.name_ar}</span>
                </button>
                {filteredModels.map((model) => (
                  <button 
                    key={model.id} 
                    onClick={() => handleApplyFilters(model.id)}
                    className="p-4 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-md border border-transparent hover:border-primary/20 transition-all text-right group"
                  >
                    <span className="block font-bold text-sm group-hover:text-primary">{model.name_ar}</span>
                    <span className="block text-[10px] text-slate-400 font-bold uppercase">{model.name_en}</span>
                  </button>
                ))}
                {selectedMake && filteredModels.length === 0 && (
                  <div className="col-span-2 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-bold text-center">
                    لا توجد موديلات جاهزة لهذه الماركة حالياً. يمكنك عرض جميع سيارات الماركة مباشرة.
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 bg-slate-50 border-t">
            <Button 
              className="w-full h-14 rounded-2xl bg-primary text-lg font-bold shadow-lg shadow-primary/20"
              onClick={() => handleApplyFilters()}
            >
              عرض النتائج المتاحة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
